import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaUser, FaGraduationCap } from "react-icons/fa";
import { MdAccessTimeFilled } from "react-icons/md";
import { getCourseOfferingApi } from "../../Api/CourseOffering";
import { getCourseTypeApi } from "../../Api/CourseType";
import { getCourseApi } from "../../Api/Course";
import { addCourseRegistrationApi } from "../../Api/Registration";
import { updateUserApi } from "../../Api/user";
import { addRelationApi, getRelationByUserIdApi, updateRelationApi } from "../../Api/Relation";
import { addUserApi } from "../../Api/user";
import Relation from "./stepper/Relation";

type CourseTypeInterface = {
    id: string;
    name: string;
    parentId: string | null;
    durationType: string;
    availability: string;
    fees: number;
    other: boolean;
    timings: [{ startTime: string, endtime: string }];
};

type CourseInterface = {
    id: string;
    name: string;
    gender: number | null
};

type CourseOfferingInterface = {
    id: string;
    courseId: string;
    courseTypeId: string;
    timings: [{ startTime: string, endtime: string }];
};

type RelationInterface = {
    registeringFor: string
    relationId: string
    UserId: string | null
    relation: string
    customRelation: string
    name: string
    gender: string
    email: string | null
    mobile: string | null
};

export const StudentRegistration = ({
    isDarkMode,
    url,
    updateUserProfile,
    useDispatch,
    useAppSelector
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    var totalMinutes;

    const [courseTypes, setCourseTypes] = useState<CourseTypeInterface[]>([]);
    const [courses, setCourses] = useState<CourseInterface[]>([]);
    const [courseOfferings, setCourseOfferings] = useState<CourseOfferingInterface[]>([]);
    const [selectedCourseType, setSelectedCourseType] = useState<string>("");
    const [selectedCourse, setSelectedCourse] = useState<string>("");

    const [timeZone, setTimeZone] = useState("");
    const [preferredTiming, setPreferredTiming] = useState("");

    const [duration, setDuration] = useState('30');
    const [customDuration, setCustomDuration] = useState('');
    const [relationDetails, setRelationDetails] = useState<RelationInterface>(
        { registeringFor: 'Self', relation: '', UserId: null, relationId: null, customRelation: '', name: '', gender: '', email: null, mobile: null }
    );
    const [userRelationData, setUserRelationData] = useState<UserDetailsInterface[]>([]);
    const [fetchedRelationData, setFetchedRelationData] = useState<any[]>([]);
    const [relationUserResponse, setRelationUserResponse] = useState<string | null>(null);

    useEffect(() => {
        if (duration !== 'custom' && customDuration) {
            setCustomDuration('');
        }
    }, [duration]);

    interface UserDetailsInterface {
        Id: string;
        Name: string;
        Email: string;
        Mobile: string;
        Gender: number | null;
        Address: string;
        RoleType: string;
    }

    const [userDetails, setUserDetails] = useState<UserDetailsInterface>({
        Id: "",
        Name: "",
        Email: "",
        Mobile: "",
        Gender: null,
        Address: "",
        RoleType: ""
    });

    const steps = ["User Details", "Course Details", "Time"];
    const [currentStep, setCurrentStep] = useState(1);

    const userProfile = useAppSelector((state) => state.userProfile?.userData);

    useEffect(() => {
        setUserDetails({
            Id: userProfile?.id || "",
            Name: userProfile?.name || "",
            Email: userProfile?.email || "",
            Mobile: userProfile?.mobile || "",
            Gender: userProfile?.gender || null,
            Address: userProfile?.address || "",
            RoleType: userProfile?.roleType.toString() || ""
        });

    }, [userProfile]);

    // Fetch course types and courses
    const handleApiCallClick = async () => {
        try {
            // RELATION AND USER PART
            const fetchedRelations: any = await getRelationByUserIdApi(url);
            setFetchedRelationData(fetchedRelations);
            const filteredRelations = fetchedRelations.map(user => user.relationUserData);
            setUserRelationData(filteredRelations);

            // COURSE PART
            const CourseOfferingData: any = await getCourseOfferingApi(url);
            const fetchedCourseTypes: any = await getCourseTypeApi(url);
            const fetchedCourses: any = await getCourseApi(url);

            const courseTypeIds = CourseOfferingData.map((detail: any) => detail.courseTypeId);

            const filteredCourseTypes = fetchedCourseTypes.filter(
                (item: any) => courseTypeIds.includes(item.id)
            ).map((item: any) => ({
                ...item,
                timings: item.timings ? item.timings.map((timing: any) => ({
                    startTime: timing.startime, // Map startime to startTime
                    endtime: timing.endtime
                })) : []
            }));
            setCourseOfferings(CourseOfferingData);
            setCourseTypes(filteredCourseTypes);

            if (selectedCourseType) {
                const filteredCourses = fetchedCourses.filter((course: CourseInterface) => {
                    const isGenderMatch =
                        course.gender === null || // For "All"
                        (userDetails.Gender === 1 && course.gender === 1) || // Match Male
                        (userDetails.Gender === 2 && course.gender === 2); // Match Female

                    return CourseOfferingData.some(
                        (detail: any) =>
                            detail.courseId === course.id &&
                            detail.courseTypeId === selectedCourseType &&
                            isGenderMatch
                    );
                });

                setCourses(filteredCourses);
            }

        } catch (error) {
            console.error("Failed to fetch Course data. Some problem occurred. Please try again.");
        }
    };

    useEffect(() => {
        handleApiCallClick();
    }, [selectedCourseType]);

    const handleSubmitClick = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCourse || !selectedCourseType) {
            alert("Please select both course type and course.");
            return;
        }

        try {
            const CourseOfferingData: any = await getCourseOfferingApi(url);
            const selectedCourseOffering = CourseOfferingData.find(
                (detail: any) => detail.courseId === selectedCourse && detail.courseTypeId === selectedCourseType
            );

            if (!selectedCourseOffering) {
                toast.error("Could not find the course details for the selected course and type.");
                return;
            }

            const courseOfferingId = selectedCourseOffering.id;
            const timeZoneAbbreviation = timeZone.split(" ")[0];
            const Duration = parseFloat((parseInt(duration === "custom" ? customDuration : duration, 10) / 60).toFixed(2));

            let relationUserId = null;

            if (relationDetails.registeringFor) {
                if (relationDetails.registeringFor === "other") {
                    relationUserId = relationUserResponse;
                } else if (relationDetails.registeringFor === "Self") {
                    relationUserId = null;
                } else {
                    relationUserId = relationDetails.registeringFor;
                }
            }
            // console.log(" relationUserId? relationUserId : userDetails.Id,", relationUserId ? relationUserId : userDetails.Id);
            const time = preferredTiming === "other"
                ? preferredTiming
                : `${formatTime(preferredTiming.split('-')[0])}-${formatTime(preferredTiming.split('-')[1])}`;
            await addCourseRegistrationApi(
                courseOfferingId,
                Duration,
                timeZoneAbbreviation,
                time,
                relationUserId ? relationUserId : userDetails.Id,
                url
            );
            setSelectedCourseType("");
            setSelectedCourse("");
            setCurrentStep(1);
            toast.success('Registered successfully');
            navigate("/Education/Registration");
        } catch (error) {

        let errorMessage = 'An error occurred during course registration.';  
    if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
        } else if (typeof error.response.data.message === 'string') { 
            errorMessage = error.response.data.message;
        }
    }

    toast.error(errorMessage);
    console.error("Registration Error:", error);
        }
    };

    function formatTime(time) {
        const [hour, minute] = time.split(':');
        const formattedHour = hour.padStart(2, '0');
        return `${formattedHour}:${minute}`;
    }

    const handleCreateRelation = async (e: React.FormEvent) => {
        e.preventDefault();

        const roleType = "1";

        const user = await addUserApi(
            url,
            relationDetails.name,
            relationDetails.email || null,
            relationDetails.mobile || null,
            null,
            roleType,
            relationDetails.gender
        ) as { data: { id: string } };
        setRelationUserResponse(user.data.id);
        const relationType = relationDetails.relation === "other" ? relationDetails.customRelation : relationDetails.relation;
        return await addRelationApi(
            url,
            relationType,
            user.data.id,
        );
    }

    const handleUserSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!relationDetails.registeringFor) {
            toast.error("Please fill in all required fields.");
            return;
        }

        if (relationDetails.registeringFor === "Self") {
            await updateUserApi(url, userDetails.Id, userDetails.Name, userDetails.Email, userDetails.Mobile, userDetails.Address, userDetails.RoleType, userDetails.Gender);
            const userData = {
                id: userDetails.Id,
                name: userDetails.Name,
                email: userDetails.Email,
                mobile: userDetails.Mobile,
                address: userDetails.Address,
                roleType: Number(userDetails.RoleType),
                gender: userDetails.Gender
            }
            await dispatch(updateUserProfile(userData));
        } else if (relationDetails.registeringFor === "other") {
            await handleCreateRelation(e);
        } else {
            const updatedRelationType = relationDetails.relation === "other" ? relationDetails.customRelation : relationDetails.relation;
            await updateUserApi(url, relationDetails.UserId, relationDetails.name, relationDetails.email, relationDetails.mobile, null, "1", parseInt(relationDetails.gender));
            if (relationDetails.UserId) {
                await updateRelationApi(relationDetails.relationId, updatedRelationType, relationDetails.UserId, url);
            }
        }
    };

    const handleCustomDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numericValue = parseInt(value, 10);
        if (!isNaN(numericValue)) {
            const roundedValue = Math.round(numericValue / 30) * 30;
            setCustomDuration(roundedValue.toString());
        } else {
            setCustomDuration('');
        }
    };

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (relationDetails.registeringFor === "Self" && (!userDetails.Name || !userDetails.Email || !userDetails.Mobile || !userDetails.Gender)) {
                toast.error('Please fill in all required fields.');
                return;
            } else if (relationDetails.registeringFor === "other" && (!relationDetails.relation || (relationDetails.relation === "other" && !relationDetails.customRelation) || !relationDetails.name || !relationDetails.gender)) {
                toast.error("Please fill in all required fields.");
                return;
            }

            handleUserSubmit({ preventDefault: () => { } } as React.FormEvent);
        } else if (currentStep === 2) {
            if (!selectedCourse || !selectedCourseType) {
                toast.error('Please fill in all required fields.');
                return;
            }
        } else if (currentStep === 3) {
            if (!duration || !timeZone || !preferredTiming) {
                toast.error('Please fill in all required fields.');
                return;
            }
        }

        setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    };

    const handlePrevStep = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const calculateOptions = (): React.ReactElement[] => {
        const selectedOffering = courseOfferings.find(offering => offering.courseId === selectedCourse && offering.courseTypeId === selectedCourseType);
        const selectedCourseTypeObj = courseTypes.find(type => type.id === selectedCourseType);
        const isFixed = selectedCourseTypeObj?.availability === "Fixed";

        const availability = selectedOffering && Array.isArray(selectedOffering.timings) && selectedOffering.timings.length > 0 ? selectedOffering.timings : selectedCourseTypeObj?.timings;
        const options: React.ReactElement[] = [];
        if (availability && Array.isArray(availability)) {
            availability.forEach((timeslot) => {
                let start, end, totalHours;
                if (timeslot.startTime === "00:00:00" && timeslot.endtime === "23:59:00") {
                    start = 0;
                    end = 24;
                    totalHours = 24;
                } else {
                    start = parseInt(timeslot.startTime.split(':')[0], 10) + parseInt(timeslot.startTime.split(':')[1], 10) / 60;
                    end = parseInt(timeslot.endtime.split(':')[0], 10) + parseInt(timeslot.endtime.split(':')[1], 10) / 60;

                    totalHours = end - start;

                    if (isFixed) {
                        const startTime = start;
                        const endTime = end;

                        const startHour = Math.floor(startTime);
                        const startMinutes = (startTime % 1) * 60;
                        const endHour = Math.floor(endTime);
                        const endMinutes = (endTime % 1) * 60;
                        options.push(
                            <option key={`${startHour}:${startMinutes}-${endHour}:${endMinutes}`} value={`${startHour}:${startMinutes.toString().padStart(2, '0')}-${endHour}:${endMinutes.toString().padStart(2, '0')}`}>
                                {`${startHour}:${startMinutes.toString().padStart(2, '0')} - ${endHour}:${endMinutes.toString().padStart(2, '0')}`}
                            </option>
                        );

                        return options;
                    }
                }

                const durationInHours = parseInt(duration === "custom" ? customDuration : duration, 10) / 60;

                for (let i = 0; i <= totalHours - durationInHours; i += 0.5) {
                    const startTime = start + i;
                    const endTime = startTime + durationInHours;

                    const startHour = Math.floor(startTime);
                    const startMinutes = (startTime % 1) * 60;
                    const endHour = Math.floor(endTime);
                    const endMinutes = (endTime % 1) * 60;
                    options.push(
                        <option key={`${startHour}:${startMinutes}-${endHour}:${endMinutes}`} value={`${startHour}:${startMinutes.toString().padStart(2, '0')}-${endHour}:${endMinutes.toString().padStart(2, '0')}`}>
                            {`${startHour}:${startMinutes.toString().padStart(2, '0')} - ${endHour}:${endMinutes.toString().padStart(2, '0')}`}
                        </option>
                    );
                }
            });
            if (courseTypes.find(type => type.id === selectedCourseType)?.other === true) {
                options.push(<option key="other" value="other">Other Timings</option>);
            }
        }
        return options;
    };

    useEffect(() => {
        calculateOptions();
    }, [duration, customDuration]);

    return (
        <div className={`w-full lg:px-[300px] overflow-auto  flex flex-col item-start p-4
            ${isDarkMode
                ? "bg-globalBg border-b border-gray-700"
                : "bg-[#ffff] border-b border-gray-200"
            }`}>

            <div className="relative px-2  w-[100%]  flex flex-col items-center justify-center mb-8">
                <div className="relative flex items-center justify-between w-[100%] m-5 ">
                    <div className="absolute left-0 top-2/4 h-0.5 w-full -translate-y-2/4 border bg-gray-300"></div>
                    <div
                        className="absolute left-0 top-2/4 h-0.5 w-full -translate-y-2/4 bg-blue-600 transition-all duration-500"
                        style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}

                    ></div>
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            className={`relative z-10 grid w-10 h-10 font-bold text-white transition-all duration-300 ${index + 1 <= currentStep ? "bg-blue-700 text-grey-300" : "bg-gray-300 text-gray-900"
                                } rounded-full place-items-center`}
                        >
                            {step === "User Details" ? (
                                <FaUser />
                            ) : step === "Course Details" ? (
                                <FaGraduationCap
                                    className={`w-6 h-6 ${index + 1 <= currentStep ? "text-white" : "text-gray-900"}`}
                                />
                            ) : step === "Time" ? (
                                <MdAccessTimeFilled className={`w-6 h-6 ${index + 1 <= currentStep ? "text-white" : "text-gray-900"}`} />
                            ) : null
                            }


                            <div className="absolute -bottom-[2.5rem] w-max">
                                <h6 className={`block font-sans text-base antialiased font-semibold leading-relaxed tracking-normal  ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>
                                    {steps[index]}
                                </h6>

                            </div>
                        </div>
                    ))}
                </div>

                {/* Step Content */}
                <div className="lg:mt-[7%] sm:mt-[10%] mt-[15%] lg:w-[30rem] md:w-[30rem] w-full items-start self-start">
                    {currentStep === 1 && (
                        <form className="w-full">
                            <Relation relationDetails={relationDetails} setRelationDetails={setRelationDetails} userDetails={userDetails} setUserDetails={setUserDetails} userRelationData={userRelationData} fetchedRelationData={fetchedRelationData} isDarkMode={isDarkMode} />
                        </form>
                    )}
                    {currentStep === 2 && (
                        <form className="w-full">
                            <div className="flex flex-col space-y-4">
                                <div className="flex flex-col space-y-2 w-full">
                                    <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>Course Type <span className="text-red-500">*</span></label>
                                    <select
                                        id="courseType"
                                        value={selectedCourseType}
                                        onChange={(e) => {
                                            setSelectedCourseType(e.target.value)
                                        }}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select Course Type</option>
                                        {courseTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>


                                <div className="flex flex-col space-y-2 w-full">
                                    <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>Course<span className="text-red-500">*</span></label>
                                    <select
                                        id="course"
                                        value={selectedCourse}
                                        onChange={(e) => setSelectedCourse(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select Course</option>
                                        {courses.map((course) => (
                                            <option key={course.id} value={course.id}>
                                                {course.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </form>
                    )}
                    {currentStep === 3 && (
                        <form className="w-full">
                            <div className="flex flex-col space-y-4">
                                <div className="flex flex-col space-y-2 w-full">
                                    <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>
                                        Duration (in minutes)<span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        id="duration"
                                        value={duration}
                                        onChange={(e) => {
                                            setDuration(e.target.value);
                                        }}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {selectedCourseType && (() => {
                                            const selectedOffering = courseOfferings.find(offering => offering.courseId === selectedCourse && offering.courseTypeId === selectedCourseType);
                                            const selectedCourseTypeObj = courseTypes.find(type => type.id === selectedCourseType);
                                            const isFixed = selectedCourseTypeObj?.availability === "Fixed";
                                            let timings: { startTime: string; endtime: string }[] | undefined = undefined;
                                            if (selectedOffering && Array.isArray(selectedOffering.timings) && selectedOffering.timings.length > 0) {
                                                timings = selectedOffering.timings;
                                            } else if (selectedCourseTypeObj && Array.isArray(selectedCourseTypeObj.timings)) {
                                                timings = selectedCourseTypeObj.timings;
                                            }
                                            if (timings && timings[0].startTime !== "00:00:00" && timings[0].endtime !== "23:59:00") {
                                                const maxTiming = timings.reduce(
                                                    (max: any, current: any) => {
                                                        const [maxStart, maxEnd] = [max.startTime, max.endtime].map((time: string) => {
                                                            const [hours, minutes] = time.split(':').map(Number);
                                                            return hours + minutes / 60;
                                                        });
                                                        const [currentStart, currentEnd] = [current.startTime, current.endtime].map((time: string) => {
                                                            const [hours, minutes] = time.split(':').map(Number);
                                                            return hours + minutes / 60;
                                                        });
                                                        const maxDuration = maxEnd - maxStart;
                                                        const currentDuration = currentEnd - currentStart;
                                                        return currentDuration > maxDuration ? current : max;
                                                    }, timings[0]);

                                                const [start, end] = [maxTiming.startTime, maxTiming.endtime].map((time: string) => {
                                                    const [hours, minutes] = time.split(':').map(Number);
                                                    return hours + minutes / 60; // Convert minutes to fractional hours
                                                });
                                                const totalHours = end - start;
                                                totalMinutes = totalHours * 60;
                                                const options: React.ReactElement[] = [];

                                                if (isFixed) {
                                                    // ✅ Fix: Auto-set correct fixed duration value in state
                                                    if (duration !== totalMinutes.toString()) {
                                                        setDuration(totalMinutes.toString());
                                                    }

                                                    options.push(
                                                        <option key={totalMinutes} value={totalMinutes}>
                                                            {totalMinutes} minutes ({(totalMinutes / 60).toFixed(1)} hour{totalMinutes > 60 ? 's' : ''})
                                                        </option>
                                                    );
                                                    return options;
                                                }
                                                else {
                                                    for (let i = 1; i <= Math.min(totalHours * 2, 6); i++) {
                                                        const minutes = i * 30;
                                                        if (minutes > totalHours * 60) {
                                                            break;
                                                        }
                                                        options.push(<option key={minutes} value={minutes}>{minutes} minutes ({(minutes / 60).toFixed(1)} hour{minutes > 60 ? 's' : ''})</option>);
                                                    }
                                                    if (totalHours > 3) {
                                                        options.push(<option key="custom" value="custom">Custom</option>);
                                                    }
                                                }

                                                return options;
                                            } else {
                                                return (
                                                    <>
                                                        <option value="30">30 minutes</option>
                                                        <option value="60">60 minutes (1 hour)</option>
                                                        <option value="90">90 minutes (1.5 hours)</option>
                                                        <option value="120">120 minutes (2 hours)</option>
                                                        <option value="150">150 minutes (2.5 hours)</option>
                                                        <option value="180">180 minutes (3 hours)</option>
                                                        <option value="custom">Custom</option>
                                                    </>
                                                );
                                            }
                                        })()}
                                    </select>
                                    {duration === 'custom' && (
                                        <div className="mb-4">
                                            <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>
                                                Custom Duration (in minutes)
                                            </label>
                                            <input
                                                type="number"
                                                id="customDuration"
                                                value={customDuration}
                                                onChange={handleCustomDurationChange}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter duration (multiple of 30)"
                                                min="30"
                                                max={totalMinutes}
                                                step="30"
                                                required={duration === 'custom'}
                                            />
                                            <p className="text-sm text-gray-600 mt-1">Duration will be rounded to the nearest 30 minutes</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col space-y-2 w-full">
                                    <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>
                                        Select Time Zone<span className="text-red-500">*</span>
                                    </label>
                                    <ul>
                                        <select
                                            required
                                            id="timeZone"
                                            value={timeZone}
                                            onChange={(e) => setTimeZone(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select Time Zone</option>
                                            <option value="IST - Indian Standard Time">IST - Indian Standard Time (UTC+5:30)</option>
                                            {/* {timeZones.map((zone, index) => (
                                                <option
                                                    key={index} value={zone.abbreviation}>{zone.abbreviation} - {zone.fullName} ({zone.offset})</option>
                                            ))} */}
                                        </select>
                                    </ul>
                                </div>

                                <div className="flex flex-col space-y-2 w-full">
                                    <label className={`block text-sm font-medium text-black dark:text-gray-200 mb-2 ${isDarkMode ? "text-gray-100" : " text-gray-700"}`}>
                                        Preferred Timings<span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        id="preferredTimings"
                                        value={preferredTiming}
                                        onChange={(e) => setPreferredTiming(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Preferred Timing</option>
                                        {calculateOptions()}
                                    </select>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
                {/* Navigation Buttons */}
                <div className="flex w-full gap-4 mt-10">
                    {currentStep !== 1 && (
                        <button
                            className="text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2"
                            // className="select-none rounded-lg bg-blue-600 py-3 px-6 text-center align-middle font-sans text-xs font-bold text-white shadow-md shadow-gray-900/10 transition-all hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                            onClick={handlePrevStep}
                            disabled={currentStep === 1}
                        >
                            Back
                        </button>
                    )}

                    {currentStep < steps.length ? (
                        <button
                            type="button"
                            onClick={handleNextStep}
                            disabled={currentStep === steps.length}
                            className="text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2"
                        >
                            {currentStep === 1 ? "Save and Continue" : "Next"}
                        </button>

                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmitClick}
                            className="text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2"
                        >

                            Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

